import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Settings,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  Car,
  Users,
  Clock,
  History,
  AlertCircle,
} from 'lucide-react';
import { revendaMaisApi } from '../../services/integracoesApi';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader } from '../../components/ui';
import { Panel, Card, Badge, Button, EmptyState } from '../../design-system';
import type {
  IntegracaoConfigDto,
  IntegracaoStatusDto,
  IntegracaoHistoricoDto,
  ImportacaoResultDto,
} from '../../types/integracoes';

const configSchema = z.object({
  username: z.string().min(1, 'Usuário é obrigatório'),
  password: z.string().min(1, 'Senha é obrigatória'),
  autoSincronizar: z.boolean().optional(),
  importarVeiculos: z.boolean().optional(),
  importarClientes: z.boolean().optional(),
});

type ConfigFormData = z.infer<typeof configSchema>;

const inputClass =
  'w-full h-10 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:outline-none';
const labelClass = 'block text-sm font-medium text-slate-700 mb-1';
const checkboxClass = 'rounded border-slate-300 text-primary-600 focus:ring-primary-500';

function isConfigDto(data: IntegracaoConfigDto | IntegracaoStatusDto): data is IntegracaoConfigDto {
  return 'id' in data && typeof data.id === 'number';
}

export default function RevendaMaisConfig() {
  const { showSuccess, showApiError, showError } = useNotification();
  const [config, setConfig] = useState<IntegracaoConfigDto | IntegracaoStatusDto | null>(null);
  const [historico, setHistorico] = useState<IntegracaoHistoricoDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState<'veiculos' | 'clientes' | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<'config' | 'historico'>('config');
  const [importResult, setImportResult] = useState<ImportacaoResultDto | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConfigFormData>({
    resolver: zodResolver(configSchema),
    defaultValues: {
      importarVeiculos: true,
      importarClientes: true,
      autoSincronizar: false,
    },
  });

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, []);

  const loadData = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const [configRes, historicoRes] = await Promise.all([
        revendaMaisApi.getConfig(signal),
        revendaMaisApi.getHistorico({ page: 1, pageSize: 10 }, signal),
      ]);

      setConfig(configRes);
      setHistorico(historicoRes.items);

      if (isConfigDto(configRes)) {
        reset({
          username: '',
          password: '',
          autoSincronizar: configRes.autoSincronizar,
          importarVeiculos: configRes.importarVeiculos,
          importarClientes: configRes.importarClientes,
        });
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'CanceledError') {
        showApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: ConfigFormData) => {
    try {
      setSaving(true);
      await revendaMaisApi.createConfig({
        username: data.username,
        password: data.password,
        autoSincronizar: data.autoSincronizar ?? false,
        importarVeiculos: data.importarVeiculos ?? true,
        importarClientes: data.importarClientes ?? true,
      });
      showSuccess('Configuração salva com sucesso!');
      await loadData();
      // Limpar campos de senha após salvar
      reset({
        ...data,
        username: '',
        password: '',
      });
    } catch (error) {
      showApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestarConexao = async () => {
    try {
      setTesting(true);
      const result = await revendaMaisApi.testarConexao();
      if (result.sucesso) {
        showSuccess(result.mensagem);
      } else {
        showError(result.mensagem);
      }
      await loadData();
    } catch (error) {
      showApiError(error);
    } finally {
      setTesting(false);
    }
  };

  const handleImportarVeiculos = async () => {
    try {
      setImporting('veiculos');
      setImportResult(null);
      const result = await revendaMaisApi.importarVeiculos();
      setImportResult(result);
      if (result.sucesso) {
        showSuccess(result.mensagem);
      } else {
        showError(result.mensagem);
      }
      await loadData();
    } catch (error) {
      showApiError(error);
    } finally {
      setImporting(null);
    }
  };

  const handleImportarClientes = async () => {
    try {
      setImporting('clientes');
      setImportResult(null);
      const result = await revendaMaisApi.importarClientes();
      setImportResult(result);
      if (result.sucesso) {
        showSuccess(result.mensagem);
      } else {
        showError(result.mensagem);
      }
      await loadData();
    } catch (error) {
      showApiError(error);
    } finally {
      setImporting(null);
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'CONECTADO':
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case 'ERRO':
        return <XCircle className="h-5 w-5 text-rose-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
    }
  };

  const getStatusTone = (status: string): 'neutral' | 'success' | 'danger' | 'info' => {
    const tones: Record<string, 'neutral' | 'success' | 'danger' | 'info'> = {
      CONCLUIDO: 'success',
      ERRO: 'danger',
      EM_ANDAMENTO: 'info',
    };
    return tones[status] || 'neutral';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const isConfigured = config && isConfigDto(config);

  return (
    <div>
      <PageHeader
        title="Integração RevendaMais"
        subtitle="Configure a integração com o sistema RevendaMais para importar veículos e clientes automaticamente"
      />

      {/* Status Card */}
      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {getStatusIcon(config?.statusConexao)}
            <div>
              <h3 className="font-semibold text-slate-900">
                Status: {config?.statusConexao || 'NÃO CONFIGURADO'}
              </h3>
              {config?.ultimoTeste && (
                <p className="text-sm text-slate-500">
                  Último teste: {formatDate(config.ultimoTeste)}
                </p>
              )}
              {config?.ultimoErro && (
                <p className="text-sm text-rose-500">{config.ultimoErro}</p>
              )}
            </div>
          </div>

          {isConfigured && (
            <div className="flex gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold tabular-nums text-primary-600">
                  {(config as IntegracaoConfigDto).veiculosImportados}
                </div>
                <div className="text-xs text-slate-500">Veículos</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold tabular-nums text-primary-600">
                  {(config as IntegracaoConfigDto).clientesImportados}
                </div>
                <div className="text-xs text-slate-500">Clientes</div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="-mb-px flex gap-8">
          <button
            onClick={() => setActiveTab('config')}
            className={`py-4 px-1 border-b-2 font-semibold text-sm transition-colors ${
              activeTab === 'config'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <Settings className="h-4 w-4 inline-block mr-2" />
            Configuração
          </button>
          <button
            onClick={() => setActiveTab('historico')}
            className={`py-4 px-1 border-b-2 font-semibold text-sm transition-colors ${
              activeTab === 'historico'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <History className="h-4 w-4 inline-block mr-2" />
            Histórico
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'config' && (
        <div className="space-y-6">
          {/* Formulário de Configuração */}
          <Panel title="Credenciais de Acesso" icon={<Settings className="h-4 w-4" />}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Usuário RevendaMais
                  </label>
                  <input
                    type="text"
                    {...register('username')}
                    placeholder={isConfigured ? '••••••••' : 'Digite seu usuário'}
                    className={inputClass}
                  />
                  {errors.username && (
                    <p className="text-rose-500 text-sm mt-1">{errors.username.message}</p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Senha</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...register('password')}
                      placeholder={isConfigured ? '••••••••' : 'Digite sua senha'}
                      className={`${inputClass} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-rose-500 text-sm mt-1">{errors.password.message}</p>
                  )}
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Opções de Importação</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      {...register('importarVeiculos')}
                      className={checkboxClass}
                    />
                    <span className="text-sm text-slate-700">Importar veículos</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      {...register('importarClientes')}
                      className={checkboxClass}
                    />
                    <span className="text-sm text-slate-700">Importar clientes</span>
                  </label>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Sincronização Automática</h3>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    {...register('autoSincronizar')}
                    className={checkboxClass}
                  />
                  <span className="text-sm text-slate-700">Sincronizar automaticamente todos os dias às 1:00 AM</span>
                </label>
                <p className="text-xs text-slate-500 mt-2 ml-6">
                  Quando ativado, o sistema irá importar automaticamente os veículos e/ou clientes selecionados acima.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Settings className="h-4 w-4" />
                  )}
                  Salvar Configuração
                </Button>

                {isConfigured && (
                  <Button type="button" variant="secondary" onClick={handleTestarConexao} disabled={testing}>
                    {testing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    Testar Conexão
                  </Button>
                )}
              </div>
            </form>
          </Panel>

          {/* Ações de Importação */}
          {isConfigured && config.statusConexao === 'CONECTADO' && (
            <Panel title="Importação de Dados" icon={<RefreshCw className="h-4 w-4" />}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-100">
                      <Car className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Veículos</h3>
                      {isConfigured && (config as IntegracaoConfigDto).ultimaSyncVeiculos && (
                        <p className="text-xs text-slate-500">
                          Última sync: {formatDate((config as IntegracaoConfigDto).ultimaSyncVeiculos)}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={handleImportarVeiculos}
                    disabled={importing !== null}
                    className="w-full"
                  >
                    {importing === 'veiculos' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        Importar Veículos
                      </>
                    )}
                  </Button>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-100">
                      <Users className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Clientes</h3>
                      {isConfigured && (config as IntegracaoConfigDto).ultimaSyncClientes && (
                        <p className="text-xs text-slate-500">
                          Última sync: {formatDate((config as IntegracaoConfigDto).ultimaSyncClientes)}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={handleImportarClientes}
                    disabled={importing !== null}
                    className="w-full"
                  >
                    {importing === 'clientes' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        Importar Clientes
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Resultado da Importação */}
              {importResult && (
                <div
                  className={`mt-4 p-4 rounded-xl ${
                    importResult.sucesso ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {importResult.sucesso ? (
                      <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-rose-600 mt-0.5" />
                    )}
                    <div>
                      <p className={`font-semibold ${importResult.sucesso ? 'text-emerald-800' : 'text-rose-800'}`}>
                        {importResult.mensagem}
                      </p>
                      <div className="mt-2 text-sm text-slate-600 grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <span className="text-slate-500">Processados:</span>{' '}
                          <strong className="tabular-nums">{importResult.registrosProcessados}</strong>
                        </div>
                        <div>
                          <span className="text-slate-500">Criados:</span>{' '}
                          <strong className="tabular-nums text-emerald-600">{importResult.registrosCriados}</strong>
                        </div>
                        <div>
                          <span className="text-slate-500">Atualizados:</span>{' '}
                          <strong className="tabular-nums text-sky-600">{importResult.registrosAtualizados}</strong>
                        </div>
                        <div>
                          <span className="text-slate-500">Erros:</span>{' '}
                          <strong className="tabular-nums text-rose-600">{importResult.registrosErro}</strong>
                        </div>
                      </div>
                      {importResult.duracaoSegundos > 0 && (
                        <p className="mt-1 text-xs text-slate-500">
                          <Clock className="h-3 w-3 inline-block mr-1" />
                          Duração: {importResult.duracaoSegundos} segundos
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Panel>
          )}
        </div>
      )}

      {activeTab === 'historico' && (
        <Card className="overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Operação
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Registros
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Duração
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {historico.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState>Nenhum histórico de sincronização encontrado</EmptyState>
                  </td>
                </tr>
              ) : (
                historico.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {formatDate(item.dataInicio)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {item.tipoOperacao.replace(/_/g, ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge tone={getStatusTone(item.status)}>{item.status}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      <span className="text-emerald-600">{item.registrosCriados} novos</span>
                      {' / '}
                      <span className="text-sky-600">{item.registrosAtualizados} atualizados</span>
                      {item.registrosErro > 0 && (
                        <>
                          {' / '}
                          <span className="text-rose-600">{item.registrosErro} erros</span>
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 tabular-nums">
                      {item.duracaoSegundos ? `${item.duracaoSegundos}s` : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
